import { Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import {
  getCachedPlanetTexture,
  getPlanetTexture,
} from '../components/planet/planetTerrainClient';

// Planet data from server
export interface PlanetData {
  id: string;
  name: string;
  systemId: string;
  x: number;
  y: number;
  radius: number;
  planetType: string;
  orbitalDistance?: number;
  orbitalSpeed?: number;
  orbitalAngle?: number;
  profileSeed?: number;
  clusterDensity?: number;
  /** Profile size multiplier — picks the hex mesh frequency for the sprite. */
  sizeMultiplier?: number;
}

// Label colour per planet type
const PLANET_LABEL_COLOR: Record<string, number> = {
  terran:   0x66cc88,
  barren:   0xaa8866,
  volcanic: 0xff6644,
  ice:      0xaaddff,
  gas:      0xffcc66,
  rocky:    0x999999,
};

/**
 * Derive a deterministic numeric seed from a planet UUID string.
 * Used as fallback when the server hasn't persisted a profile_seed yet.
 */
function seedFromId(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) || 1; // ensure > 0
}

// Visual scale: how big the planet renders relative to logical radius
const PLANET_VISUAL_SCALE = 1.5;

export class Planet extends Container {
  public planetId: string;
  public planetName: string;
  public systemId: string;
  public planetType: string;
  public planetRadius: number;
  public orbitalDistance: number;
  public orbitalSpeed: number;
  public visualRadius: number;

  private planetSprite: Sprite;
  private zoneGraphics: Graphics;
  private placeholderGfx: Graphics | null = null;
  private nameText: Text;
  private disposed: boolean = false;

  constructor(data: PlanetData) {
    super();
    this.position.set(data.x, data.y);
    this.planetId = data.id;
    this.planetName = data.name;
    this.systemId = data.systemId;
    this.planetType = data.planetType;
    this.planetRadius = data.radius;
    this.visualRadius = data.radius * PLANET_VISUAL_SCALE;
    this.orbitalDistance = data.orbitalDistance || 5000;
    this.orbitalSpeed = data.orbitalSpeed || 0.01;

    this.sortableChildren = true;

    // Landing zone (drawn behind planet)
    this.zoneGraphics = new Graphics();
    this.zoneGraphics.zIndex = -1;
    this.addChild(this.zoneGraphics);
    this.drawZones();

    // Planet sprite (texture filled in below — either cached or via async bake)
    this.planetSprite = new Sprite();
    this.planetSprite.anchor.set(0.5);
    this.planetSprite.zIndex = 0;
    this.addChild(this.planetSprite);

    // Resolve seed / density / mesh frequency input
    const seed = (data.profileSeed && data.profileSeed > 0)
      ? data.profileSeed
      : seedFromId(data.id);
    const density = data.clusterDensity ?? 0.5;
    const diameter = Math.round(this.visualRadius * 2);
    // size_multiplier picks the hex mesh frequency (small=G(6), medium=G(8), large=G(10)).
    // Fallback to 1.0 (medium) when the server hasn't sent it (legacy snapshots).
    const sizeMultiplier = data.sizeMultiplier ?? 1.0;

    // L1 fast path: in-memory Pixi Texture cache → use synchronously, no placeholder.
    const cached = getCachedPlanetTexture(seed, diameter);
    if (cached) {
      this.applyTerrainTexture(cached);
    } else {
      // Show a tinted placeholder circle so the planet is visible immediately.
      // The real texture is baked off-thread (Web Worker + OffscreenCanvas);
      // if a previous session baked the same seed it comes back from IndexedDB
      // in ~10ms; otherwise the worker pays the noise-field cost without
      // blocking the main thread.
      const tint = PLANET_LABEL_COLOR[this.planetType] ?? PLANET_LABEL_COLOR.rocky;
      const placeholder = new Graphics();
      placeholder.circle(0, 0, this.visualRadius);
      placeholder.fill({ color: tint, alpha: 0.55 });
      placeholder.zIndex = 0;
      this.addChild(placeholder);
      this.placeholderGfx = placeholder;

      void getPlanetTexture({ seed, density, planetType: this.planetType, diameter, sizeMultiplier }).then((texture) => {
        if (this.disposed) return;
        this.applyTerrainTexture(texture);
        if (this.placeholderGfx) {
          this.placeholderGfx.destroy();
          this.placeholderGfx = null;
        }
      }).catch(() => {
        // getPlanetTexture is designed to never reject (sync fallback inside),
        // but guard anyway so a thrown error doesn't crash the world.
      });
    }

    // Name label
    const labelColor = PLANET_LABEL_COLOR[this.planetType] ?? PLANET_LABEL_COLOR.rocky;
    this.nameText = new Text({
      text: this.planetName.toUpperCase(),
      style: {
        fontFamily: 'monospace',
        fontSize: 14,
        fill: labelColor,
        stroke: { color: 0x0a1628, width: 3 },
      },
    });
    this.nameText.anchor.set(0.5);
    this.nameText.position.set(0, -this.visualRadius - 20);
    this.nameText.zIndex = 2;
    this.addChild(this.nameText);
  }

  private applyTerrainTexture(texture: Texture): void {
    this.planetSprite.texture = texture;
    const texSize = Math.max(texture.width, texture.height);
    this.planetSprite.scale.set((this.visualRadius * 2) / texSize);
  }

  setSunPosition(_sx: number, _sy: number): void {
    // Shadow baked into terrain texture
  }

  private drawZones(): void {
    const landingRadius = this.planetRadius * 2.0;
    const color = 0x44ddaa;

    // Landing zone - dashed circle
    const circumference = 2 * Math.PI * landingRadius;
    const dashLength = 25, gapLength = 15;
    const totalSegments = Math.floor(circumference / (dashLength + gapLength));
    const anglePerSegment = (2 * Math.PI) / totalSegments;
    const dashAngle = (dashLength / circumference) * 2 * Math.PI;

    for (let i = 0; i < totalSegments; i++) {
      const startAngle = i * anglePerSegment;
      this.zoneGraphics.arc(0, 0, landingRadius, startAngle, startAngle + dashAngle);
      this.zoneGraphics.stroke({ width: 2, color, alpha: 0.35 });
    }

    // Faint fill
    this.zoneGraphics.circle(0, 0, landingRadius);
    this.zoneGraphics.fill({ color, alpha: 0.03 });
  }

  update(_delta: number): void {}

  updateFromServer(data: Partial<PlanetData>): void {
    if (data.name !== undefined) {
      this.planetName = data.name;
      this.nameText.text = data.name.toUpperCase();
    }
    if (data.x !== undefined) this.x = data.x;
    if (data.y !== undefined) this.y = data.y;
  }

  public getPosition() { return { x: this.x, y: this.y }; }

  public getDistanceTo(x: number, y: number): number {
    const dx = x - this.x, dy = y - this.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  public isInLandingRange(x: number, y: number): boolean {
    return this.getDistanceTo(x, y) < this.planetRadius * 2.0;
  }

  destroy(): void {
    this.disposed = true;
    this.planetSprite.destroy();
    this.zoneGraphics.destroy();
    if (this.placeholderGfx) {
      this.placeholderGfx.destroy();
      this.placeholderGfx = null;
    }
    this.nameText.destroy();
    super.destroy();
  }
}
